import type { ApolloQueryResult } from '@apollo/client';
import React from 'react';
import styled from 'styled-components';

import { CropImage } from '@/components/CropImage/CropImage';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import type { ContentfulSelectorModalByMarketingPartnerQuery } from '@/graphql/contentful/api';
import type { ProductsByMarketingPartnerQuery } from '@/graphql/saleor/api';
import { generateDataTestId } from '@/lib/tests';

interface MarketingSelectorButtonProps {
  marketingPartnerProductsResult?: ApolloQueryResult<ProductsByMarketingPartnerQuery>;
  marketingSelectorModalResult?:
    | NonNullable<
        ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
      >['items'][0]
    | null;
  setIsMarketingSelectorModalOpen: (isOpen: boolean) => void;
  sku?: string | null;
}

const ButtonContainer = styled.div`
  display: inline-flex;
  margin-bottom: 1em;
  cursor: pointer;
`;

const ImageContainer = styled.div`
  border: 4px solid #fff;
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  height: 36px;
  width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  backface-visibility: hidden;
  transform: translateZ(0);
  perspective: 1000;
  will-change: transform;
  -webkit-backface-visibility: hidden;
  -webkit-transform: translateZ(0);
  -webkit-perspective: 1000;
`;

const MarketingSelectorButton: React.FC<MarketingSelectorButtonProps> = ({
  marketingPartnerProductsResult,
  marketingSelectorModalResult,
  setIsMarketingSelectorModalOpen,
  sku,
}) => {
  const products = marketingPartnerProductsResult?.data?.products?.edges || [];

  const validItems: {
    contentfulItem: any;
    name: string | undefined;
    sku: string | undefined;
  }[] = [];

  for (const item of products) {
    const sku = item?.node?.slug;
    const attributes = item?.node?.attributes;

    const marketingCampaignAttribute = attributes?.find(
      (attr) => attr?.attribute?.slug === 'com-marketing-campaign',
    );
    const name = marketingCampaignAttribute?.values[0]?.name as
      | string
      | undefined;

    if (name) {
      const contentfulItem =
        marketingSelectorModalResult?.linksCollection?.items.find(
          (link) => link?.reference === name,
        );
      validItems.push({ name, sku, contentfulItem });
    }
  }

  const currentItem = validItems.find((item) => item.sku === sku);

  return (
    <ButtonContainer
      data-test-id='MarketingSelectorButtonContainer'
      onClick={() => setIsMarketingSelectorModalOpen(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          setIsMarketingSelectorModalOpen(true);
        }
      }}
      role='button'
      tabIndex={0}
    >
      <ImageContainer data-test-id='ChangeStrapButtonImageContainer'>
        <CropImage
          alt={currentItem?.contentfulItem?.image?.description || ''}
          coords={{ x1: 0, y1: 0, x2: 0, y2: 0 }}
          data-test-id='ChangeStrapButtonCropImage'
          height={41}
          loading='lazy'
          mobileWidth={30}
          originalImageDimensions={{
            x: 768,
            y: 1024,
          }}
          sizes='50vw'
          src={currentItem?.contentfulItem?.image?.url || ''}
          useLoader={false}
          width={30}
          {...generateDataTestId('productCard', 'image')}
        />
      </ImageContainer>
      <LegacyButton
        label={marketingSelectorModalResult?.title}
        ml={1}
        size='l'
        variant='link'
        width='auto'
      >
        <Icon name='chevronRight' />
      </LegacyButton>
    </ButtonContainer>
  );
};

export default MarketingSelectorButton;
